import { inject, Injectable } from '@angular/core';
import { group as radashGroup, unique as radashUnique } from 'radash';
import { firstValueFrom } from 'rxjs';

import {
    Cassettes,
    SelectedNode,
    SynopticPlanItem,
} from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.models';
import {
    CableDirectionEnum,
    FiberingResult,
    PlanTypeEnum,
    SynopticBayPlanItem,
    SynopticBoxPlanCable,
    SynopticBoxPlanCableBySide,
    SynopticBoxPlanItem,
    SynopticPositionListValues,
    SynopticService,
} from '@connect-services/client/sdk/api';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

@Injectable({
    providedIn: 'root',
})
export class SynopticPlanEditApiCallerService {
    private readonly projectEditModeService = inject(ProjectEditModeService);
    private readonly snackBarService = inject(SnackBarService);
    private readonly synopticService = inject(SynopticService);

    public get editSessionId(): string {
        return this.projectEditModeService.getEditSession()?.id ?? '';
    }

    public async checkFibering(
        nodeId: string,
        depositId: string,
        plan: SynopticPlanItem[],
        planType: PlanTypeEnum,
        zoneId: string,
    ): Promise<string[]> {
        try {
            const checkingResult = await firstValueFrom(
                this.synopticService.synopticControllerCheckFibering({
                    boxOrBayId: nodeId,
                    depositId: depositId,
                    fiberingDto: {
                        editSessionId: this.editSessionId,
                        plan: plan,
                        type: planType,
                        znUuid: zoneId,
                    },
                }),
            );

            if (!checkingResult.success) {
                return checkingResult.errors;
            }
            this.snackBarService.openSuccessSnackBar('synopticPlan.checkFiberingSuccess');

            return [];
        } catch {
            return [];
        }
    }

    public async getBayPlan(bayId: string, depositId: string, znUuid: string): Promise<SynopticBayPlanItem[]> {
        try {
            return await firstValueFrom(
                this.synopticService.synopticControllerGetBayPlan({
                    bayId: bayId,
                    depositId: depositId,
                    editSessionId: this.editSessionId,
                    znUuid: znUuid,
                }),
            );
        } catch {
            return [];
        }
    }

    public async getBoxPlan(boxId: string, depositId: string, znUuid: string): Promise<Cassettes<SynopticBoxPlanItem>> {
        try {
            const plan = await firstValueFrom(
                this.synopticService.synopticControllerGetBoxPlan({
                    boxId: boxId,
                    depositId: depositId,
                    editSessionId: this.editSessionId,
                    znUuid: znUuid,
                }),
            );

            return radashGroup(
                plan,
                (item: SynopticBoxPlanItem) => item.cassette ?? '',
            ) as Cassettes<SynopticBoxPlanItem>;
        } catch {
            return {};
        }
    }

    public async getCableData(
        depositId: string,
        businessIdentifier: string,
        side: CableDirectionEnum,
    ): Promise<SynopticBoxPlanCable | null> {
        if (!businessIdentifier) {
            return null;
        }

        try {
            return await firstValueFrom(
                this.synopticService.synopticControllerGetCable({
                    cableEtiquet: businessIdentifier,
                    depositId: depositId,
                    editSessionId: this.editSessionId,
                    side: side,
                }),
            );
        } catch {
            return null;
        }
    }

    public async getPositionListValues(depositId: string): Promise<SynopticPositionListValues> {
        try {
            return await firstValueFrom(
                this.synopticService.synopticControllerGetPositionsFormData({
                    depositId: depositId,
                }),
            );
        } catch {
            return { assignments: [], engineeringTypes: [], functions: [] };
        }
    }

    public async getSideCables(
        nodeOrIds: SelectedNode | string[],
        depositId: string,
    ): Promise<SynopticBoxPlanCableBySide> {
        const ids = Array.isArray(nodeOrIds) ? nodeOrIds : [nodeOrIds.dataNode.graceIdentifier ?? ''];
        const uniqueIds = [...new Set(ids.filter(Boolean))];
        if (!uniqueIds.length) {
            return { enteringCables: [], outgoingCables: [] };
        }

        const cablesById = await Promise.all(
            uniqueIds.map(async (id: string): Promise<SynopticBoxPlanCableBySide> => {
                try {
                    return await firstValueFrom(
                        this.synopticService.synopticControllerGetBoxCables({
                            boxId: id,
                            depositId: depositId,
                            editSessionId: this.editSessionId,
                        }),
                    );
                } catch {
                    return { enteringCables: [], outgoingCables: [] };
                }
            }),
        );

        return {
            enteringCables: radashUnique(
                cablesById.flatMap((cables: SynopticBoxPlanCableBySide) => cables.enteringCables),
                (cable: SynopticBoxPlanCable) => cable.code,
            ),
            outgoingCables: radashUnique(
                cablesById.flatMap((cables: SynopticBoxPlanCableBySide) => cables.outgoingCables),
                (cable: SynopticBoxPlanCable) => cable.code,
            ),
        };
    }

    public async validateFibering(
        nodeId: string,
        depositId: string,
        plan: SynopticPlanItem[],
        planType: PlanTypeEnum,
        zoneId: string,
    ): Promise<FiberingResult> {
        try {
            const validationResult = await firstValueFrom(
                this.synopticService.synopticControllerValidateFibering({
                    boxOrBayId: nodeId,
                    depositId: depositId,
                    fiberingDto: {
                        editSessionId: this.editSessionId,
                        plan: plan,
                        type: planType,
                        znUuid: zoneId,
                    },
                }),
            );

            if (validationResult.success) {
                this.snackBarService.openSuccessSnackBar('synopticPlan.checkFiberingSuccess');
            } else {
                this.snackBarService.openErrorSnackBar('synopticPlan.checkFiberingError');
            }

            return validationResult;
        } catch {
            return { errors: [], message: '', success: false };
        }
    }
}
